import { useCallback, useMemo } from 'react';

import type { ColumnFilterWithDisplayLabel, FilterConfig } from '../TableHeader';

export interface ActiveFilter {
    id: string;
    value: string;
    displayLabel: string;
    config: FilterConfig;
    isPermanent: boolean;
}

const isValidFilterValue = (value: string): boolean => {
    return !!value && value !== 'all';
};

/**
 * Custom hook to manage active filters logic.
 */
const useActiveFilters = (
    filters: FilterConfig[],
    columnFilters: ColumnFilterWithDisplayLabel[],
    onFiltersChange: (filters: ColumnFilterWithDisplayLabel[]) => void
) => {
    // Get all active (selected) filters
    const activeFilters = useMemo((): ActiveFilter[] => {
        return filters.reduce<ActiveFilter[]>((acc, filterConfig) => {
            const columnFilter = columnFilters.find(f => f.id === filterConfig.id);
            const value = (columnFilter?.value as string) ?? '';
            const displayLabel = columnFilter?.displayLabel ?? value;

            if (isValidFilterValue(value)) {
                acc.push({
                    id: filterConfig.id,
                    value,
                    displayLabel,
                    config: filterConfig,
                    isPermanent: !!filterConfig.permanent,
                });
            }

            return acc;
        }, []);
    }, [filters, columnFilters]);

    const permanentFilters = useMemo(() => activeFilters.filter(f => f.isPermanent), [activeFilters]);
    const removableFilters = useMemo(() => activeFilters.filter(f => !f.isPermanent), [activeFilters]);

    // Updates a filter with a new value if valid, or removes the filter if the value is empty or 'all'.
    const updateFilter = useCallback(
        (filterId: string, value: string, displayLabel?: string) => {
            const newFilters = columnFilters.filter(f => f.id !== filterId);

            if (isValidFilterValue(value)) {
                newFilters.push({ id: filterId, value, displayLabel });
            }

            onFiltersChange(newFilters);
        },
        [columnFilters, onFiltersChange]
    );

    // Handle clearing of all selected filters while keeping permanent filters
    const clearAllFilters = useCallback(() => {
        const permanentFilterIds = new Set(permanentFilters.map(f => f.id));
        const filtersToKeep = columnFilters.filter(filter => permanentFilterIds.has(filter.id));

        onFiltersChange(filtersToKeep);
    }, [columnFilters, permanentFilters, onFiltersChange]);

    return {
        activeFilters,
        permanentFilters,
        removableFilters,
        updateFilter,
        clearAllFilters,
    };
};

export default useActiveFilters;
